// These schemas will eventually come from a TS client generated from zones-service, but
// that doesn't exist yet.
import { z as zod } from "zod";

export const fieldLayerReference = zod.object({
  fieldLayerId: zod.string().uuid(),
  layerType: zod.string(),
});

export const yieldGoal = zod.object({
  unitOfMeasure: zod.string(),
  dry: zod.number(),
  dryMin: zod.number().nullish(),
  dryMax: zod.number().nullish(),
  irrigated: zod.number().nullish(),
  irrigatedMin: zod.number().nullish(),
  irrigatedMax: zod.number().nullish(),
});

export const zoneSetInfo = zod.object({
  zoneSetId: zod.string().uuid(),
  active: zod.boolean(),
  yieldGoal: yieldGoal.nullish(),
  cropSeasonId: zod.string().uuid(),
});

export const zoneSetInfoByCropSeason = zod.record(
  zod.string().uuid(),
  zoneSetInfo,
);

export const zoneSet = zod.object({
  zoneSetId: zod.string().uuid(),
  zoneSetRevisionId: zod.string().uuid(),
  operationId: zod.string().uuid(),
  fieldId: zod.string().uuid(),
  name: zod.string(),
  soilLayer: fieldLayerReference.nullable(),
  productivityLayer: fieldLayerReference,
  otherFieldLayers: zod.array(fieldLayerReference),
  preparedFieldLayers: zod.array(fieldLayerReference),
  createdOn: zod.string(),
  modifiedOn: zod.string(),
  businessCycleId: zod.string().uuid().nullable(),
  rollOverSourceZs: zod.string().uuid().nullable(),
  minPolygonSize: zod.number().nullable(),
  zoneSetInfo: zoneSetInfoByCropSeason.nullable(),
});

export const mergeSettings = zod.object({
  matchAttributes: zod.array(zod.string()).optional(),
  minimumPolygonSize: zod.number(),
});

export const getV4ZoneSetsResponse = zod.array(zoneSet);

export const generateDecisionZonesFromSmartDefaultsResponse = zod.object({
  commandIds: zod.array(zod.string()),
});

export const setActiveZoneSetResponse = zod.object({
  commandId: zod.string(),
  result: zod
    .object({
      fieldId: zod.string(),
      zoneSetId: zod.string(),
      productionCycleId: zod.string().optional(), // Not actually used, just here to complete the schema
    })
    .nullable(),
});

export const updateYieldGoalsV3PayloadItem = zod.object({
  fieldId: zod.string().uuid(),
  cropSeasonId: zod.string().uuid(),
  yieldGoal,
});
export const updateYieldGoalsV3Payload = zod.object({
  operationId: zod.string().uuid(),
  yieldGoals: zod.array(updateYieldGoalsV3PayloadItem),
});
export const updateYieldGoalsV3Response = zod.object({
  commandId: zod.string(),
  result: zod
    .object({
      yieldGoalLayerGenStatus: zod.string(),
      yieldGoalLayerKey: zod.string().uuid(),
      yieldGoalLayerRevision: zod.string(),
    })
    .nullable(),
});

export const zonesPollResult = zod.object({
  createdBy: zod.string(),
  commandId: zod.string(),
  parentCommandId: zod.string().nullable(),
  optionalCorrelationId: zod.string().nullable(),
  status: zod.string(),
  response: zod.string().nullish(),
  metadata: zod.string().nullish(),
});

export const zonesGetStatusForCommandsResponse = zod.array(zonesPollResult);

export const vrsYieldGoal = zod.object({
  operationId: zod.string().uuid(),
  fieldId: zod.string().uuid(),
  cropSeasonId: zod.string().uuid(),
  plantingPlanId: zod.string().uuid().nullish(),
  unitOfMeasure: zod.string().nullable(),
  yieldGoalDry: zod.number().nullable(),
  yieldGoalDryMin: zod.number().nullable(),
  yieldGoalDryMax: zod.number().nullable(),
  yieldGoalIrrigated: zod.number().nullable(),
  yieldGoalIrrigatedMin: zod.number().nullable(),
  yieldGoalIrrigatedMax: zod.number().nullable(),
  decisionZoneLayerKey: zod.string().nullable(),
  decisionZoneLayerRevision: zod.string().nullable(),
  // Both joined in from the assigned decision zone's `layers.layer` row, so
  // both are absent for a zone the layers stream has not mirrored yet.
  layerName: zod.string().nullish(),
  hasIrrigatedValue: zod.boolean().nullish(),
  yieldGoalLayerKey: zod.string().nullable(),
  yieldGoalLayerRevision: zod.string().nullable(),
  vrsLayerKey: zod.string().nullish(),
  vrsLayerRevision: zod.string().nullish(),
  status: zod.string().nullish(),
  createdOn: zod.string(),
  modifiedOn: zod.string(),
  fieldName: zod.string().nullish(),
  farmId: zod.string().uuid().nullish(),
  farmName: zod.string().nullish(),
  area: zod.number().nullish(),
  source: zod.string().nullish(),
  thumbnailSvgS3Key: zod
    .object({ bucket: zod.string(), key: zod.string() })
    .nullish(),
});

export const getVrsYieldGoalsResponse = zod.object({
  layerData: zod.array(vrsYieldGoal),
});
export const setActiveDecisionZoneData = zod.object({
  operationId: zod.string().uuid(),
  fieldId: zod.string().uuid(),
  cropSeasonId: zod.string().uuid(),
  decisionZoneLayer: zod.object({
    layerKey: zod.string().uuid(),
    layerRevision: zod.string(),
    s3Key: zod.string().optional(),
  }),
  isIrrigated: zod.boolean().optional(),
});
export const setActiveDecisionZoneResponse = zod.object({
  commandId: zod.string().uuid(),
  result: zod.object({
    yieldGoalLayerGenStatus: zod.string(),
    yieldGoalLayerKey: zod.string().uuid(),
    yieldGoalLayerRevision: zod.string(),
  }),
});

export const getMyyaListFieldops = zod.object({
  harvestType: zod.string(),
  cropYear: zod.number(),
  commodityId: zod.string().uuid(),
  commodityName: zod.string(),
  avgYield: zod.number().optional(),
  yieldUnit: zod.string(),
  coverageRatio: zod.number().optional(),
});

export const getMyyaListFieldopsResponse = zod.array(getMyyaListFieldops);

export const decisionZonesSmartDefaultsResponse = zod.object({
  commandId: zod.string().uuid().nullish(),
  result: zod
    .object({
      yieldGoalLayerGenStatus: zod
        .enum(["None", "Create", "Update", "Error"])
        .default("None"),
      yieldGoalLayerKey: zod.string().uuid().nullish(),
      yieldGoalLayerRevision: zod.string().nullish(),
    })
    .nullish(),
});

export const initiateMyyaResponse = zod.object({
  statusCode: zod.string(),
  message: zod.string(),
  token: zod.string().uuid(),
});
export const analysisParameters = zod.object({
  lowThreshold: zod.number(),
  highThreshold: zod.number(),
  stabilityCutoff: zod.number(),
});

export const myyaRequest = zod.object({
  fieldId: zod.string(),
  fieldRevisionId: zod.string(),
  layerName: zod.string(),
  layersStore: zod.boolean().optional(),
  fieldOpReferences: getMyyaListFieldopsResponse,
  analysisParameters: analysisParameters,
  irrigationLayer: zod.object({
    layerKey: zod.string().uuid(),
    layerRevision: zod.string().nullish(),
    s3Key: zod.string().optional(),
  }).optional(),
});

// TODO: Remove V3 schema (myyaRequest) when app-vrs-remove-field-revision-id is permanently enabled
export const myyaRequestV4 = myyaRequest.omit({ fieldRevisionId: true });

const layersStoreKeyInfo = zod.object({
  layerKey: zod.string(),
  layerRevision: zod.string(),
  s3key: zod.string().optional(),
});

// TODO: check nullable value, once migration is complete
export const createDecisionZoneResponse = zod.object({
  commandId: zod.string(),
  result: layersStoreKeyInfo.nullable(),
});

const myyaLayer = zod.object({
  layerId: zod.string().uuid(),
  layerName: zod.string(),
  metric: zod.string(),
  layersStoreRef: layersStoreKeyInfo.nullish(),
  tempS3Key: zod.string().nullish(),
});

const agronomyLayerResult = zod.object({
  layers: zod.array(myyaLayer).nullish(),
});

export const myyaResult = zod.object({
  token: zod.string().optional(),
  status: zod.string(),
  state: zod.string(),
  description: zod.string(),
  agronomyLayerResults: agronomyLayerResult.nullish(),
});

export const updateVrsYieldGoalStatusPayload = zod.object({
  status: zod.string(),
  fieldIds: zod.string().array(),
  cropSeasonId: zod.string(),
})

export const createDecisionZoneV3PayloadItem = zod.object({
  operationId:  zod.string().uuid(),
  fieldId: zod.string().uuid(),
  name: zod.string(),
  mergeSettings,
  soilLayer: zod.string().optional(),
  myyaLayers: zod.object({
    myyaClassLayer: layersStoreKeyInfo,
    myyaProductivityLayer: layersStoreKeyInfo,
  }).optional(),
  ypzLayer: layersStoreKeyInfo.optional(),
  additionalLayers: zod.array(layersStoreKeyInfo).optional(),
});

export const createDecisionZoneV3Payload = createDecisionZoneV3PayloadItem;

export const activeZonesByField = zod.object({
  operationId: zod.string().uuid(),
  fieldId: zod.string().uuid(),
  cropSeasonId: zod.string().uuid(),
  cropYear: zod.number(),
  cropId: zod.string().uuid(),
  layerStoreLayerKey: zod.string().uuid(),
  layerStoreLayerRevision: zod.string(),
  createdOn: zod.string(),
  modifiedOn: zod.string(),
  isIrrigated: zod.boolean().optional(),
})

export const getV3ActiveZonesByFieldResponse = zod.array(activeZonesByField)

// Agronomy `layers.layer` row as served by zones-service
// GET /v3/zones/layers/<field_id>. Keys are camelCased by the service's
// pydantic alias generator. The table's metadata columns (consumedDate,
// sentToStreamTime) are intentionally excluded by the endpoint.
export const agronomyFieldLayer = zod.object({
  layerKey: zod.string().uuid(),
  layerRevision: zod.string(),
  operationId: zod.string().uuid(),
  fieldId: zod.string().uuid(),
  layerType: zod.string(),
  status: zod.string(),
  layerName: zod.string(),
  s3GeojsonUri: zod.string(),
  cropSeasonId: zod.string().uuid().nullish(),
  unitOfMeasure: zod.string().nullish(),
  seedVarieties: zod
    .union([zod.array(zod.any()), zod.record(zod.string(), zod.any())])
    .nullish(),
  hasIrrigatedValue: zod.boolean().nullish(),
  myyaLayerGroupId: zod.string().uuid().nullish(),
  ypzCropName: zod.string().nullish(),
  ypzNumLevels: zod.number().nullish(),
  ypzAttributeDefinitions: zod
    .union([zod.array(zod.any()), zod.record(zod.string(), zod.any())])
    .nullish(),
})

export const getV3ZonesLayersByFieldResponse = zod.array(agronomyFieldLayer)

// An operation's fields waiting on YPZ for their current boundary, as served by
// zones-service GET /v3/zones/ypz/awaiting-fields.
export const ypzAwaitingField = zod.object({
  fieldId: zod.string().uuid(),
  boundaryRevised: zod.string(),
  submitted: zod.string().nullish(),
})

export const getV3YpzAwaitingFieldsResponse = zod.array(ypzAwaitingField)
